import type {Plan} from '../plan-card/PlanCard';
import type {BillingCycle} from '../../constants/plans';
import {
  featuresTextMapping,
  getFeaturesForClientType,
  getCustomRenderer,
} from './features';
import type {FeatureToCompare, PlanToCompare} from './types';

export type FeatureDetails = {
  title: string;
  sections: Array<{
    heading?: string;
    description?: string;
    bullets?: string[];
  }>;
};

const PROFESSIONALLY_DRAWN_FLOOR_PLANS_DETAILS: FeatureDetails = {
  title: 'Professionally Drawn Floor Plans',
  sections: [
    {
      description:
        "Request a floor plan to be drawn and scaled by Cvent's professional floor plan design team",
    },
  ],
};

export const FEATURE_DETAILS_BY_KEY: Record<string, FeatureDetails> = {
  property_unique_sqft_limit: PROFESSIONALLY_DRAWN_FLOOR_PLANS_DETAILS,
  professionally_drawn_floor_plans_enabled:
    PROFESSIONALLY_DRAWN_FLOOR_PLANS_DETAILS,
  core_diagramming_enabled: {
    title: 'Core Diagramming Features',
    sections: [
      {
        heading: 'Diagramming',
        bullets: [
          'Create diagrams for your event',
          'Use an assistant to guide and simplify workflows',
        ],
      },
      {
        heading: 'Collaboration',
        bullets: [
          'Share your event with collaborators and manage their permissions',
          'Generate a public link to your diagram',
          'Format and export your diagram as a PDF',
        ],
      },
      {
        heading: 'Attendee management',
        bullets: [
          'Add attendees and manage their details',
          'Seat attendees on your diagram',
        ],
      },
    ],
  },
  diagram_efficiency_features_enabled: {
    title: 'Advanced Diagramming Features',
    sections: [
      {
        heading: 'Diagram status',
        description:
          'Add a status to your diagram to keep your team and stakeholders updated during event planning',
      },
      {
        heading: 'Favorites',
        description:
          'Save objects or groups of objects as favorites to reuse and share with your team',
      },
      {
        heading: 'Templates',
        description:
          'Fill a diagram quickly with customizable presets of tables and chairs',
      },
      {
        heading: 'Layouts',
        description:
          'Save room-specific setups of commonly used event spaces to reuse',
      },
      {
        heading: 'Table numbering',
        description:
          'Number multiple tables at once to organize attendee seating',
      },
      {
        heading: 'BEO uploader',
        description:
          'Use AI to get started on events and diagrams based on information in your BEO',
      },
    ],
  },
  attendee_management_enabled: {
    title: 'Additional Attendee Management Features',
    sections: [
      {
        heading: 'Custom data fields',
        description:
          'Customize information you collect from attendees based on your event needs',
      },
      {
        heading: 'Attendee check-in',
        description: 'Check in attendees from any device at your event',
      },
    ],
  },
};

export const getFeatureDetails = (featureKey: string | null | undefined) =>
  featureKey ? FEATURE_DETAILS_BY_KEY[featureKey] : undefined;

export const getPlansToCompareInFormat = (
  plans: Array<Plan>,
  billingCycle: BillingCycle
): Array<PlanToCompare> => {
  return plans.map((plan) => {
    const pricingOption = plan.pricing_options.find(
      (opt) => opt.billing_cycle === billingCycle
    );
    return {
      title: plan.display_name,
      type: plan.plan_type,
      price: pricingOption?.price,
    };
  });
};

export const getAllPlanFeatures = (plans: Array<Plan>, clientType?: string) => {
  const planFeaturesData: Array<FeatureToCompare> = [];

  // Get features based on client type
  const featuresToDisplay = getFeaturesForClientType(clientType);

  // Only include features that are in featuresToDisplay
  featuresToDisplay.forEach((featureKey) => {
    const featureConfig = featuresTextMapping[featureKey];
    const customRenderer = getCustomRenderer(featureKey);
    const row: FeatureToCompare = {
      featureKey,
      features: featureConfig?.displayName || featureKey,
      description: featureConfig?.description,
      showIcon: featureConfig?.showIcon ?? false,
    };

    // Add value for each plan
    plans.forEach((plan) => {
      const rawValue = plan.features[featureKey];
      // Apply custom renderer if available
      row[plan.plan_type] = customRenderer
        ? customRenderer(rawValue, plan.plan_type)
        : rawValue;
    });

    planFeaturesData.push(row);
  });

  return planFeaturesData;
};
